import React from 'react';

import AddProductButton from '../../../../../../components/add-product-button/add-product-button';
import ProductArticleStatusBanner from '../../../../../../components/product-article-status-banner/product-article-status-banner.one-dt';
import ProductAvailability from '../../../../../../components/product-availability/product-availability';
import ProductFirstPriorityDetails from '../../../../../../components/product-first-priority-details/product-first-priority-details';
import ProductListCardPromos from '../../../product-list-card-promos/product-list-card-promos';
import ProductListReviewDetails from '../../../product-list-review-details/product-list-review-details';
import ProductListStaggeredCardAxle from '../product-list-staggered-card-axle/product-list-staggered-card-axle';

import { useProductEntryDataLayerValues } from '../../../../../../shared/hooks/hooks';

import { trackAction } from '../../../../../../shared/utils/analytics/analytics';
import {
  getPrioritizedArticleStatusProduct,
  getShouldShowProductArticleStatusBanner
} from '../../../../../../shared/utils/product/product';
import { setSelectedProductAvailabilityStore } from '../../../../utils/product-list-page-multistore-utils';

import { ProductData } from '../../../../../../shared/types/graphql-types';
import { ProductItem } from '../../../../../../shared/types/product';

import styles from './product-list-staggered-card-actions.scss';

interface ProductListStaggeredCardActionsProps {
  defaultQuantity?: number;
  isTreadwellTabSelected?: boolean;
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick: () => void;
  onQuantityChange: (value: number) => void;
  onRearQuantityChange: (value: number) => void;
  product: ProductData;
  productDetailUrl?: string;
  quantity: number;
  rearQuantity: number;
  showFirstPriorityDetails?: boolean;
  showReportCard?: boolean;
  totalQuantity: number;
}

function ProductListStaggeredCardActions(
  props: ProductListStaggeredCardActionsProps
) {
  const {
    defaultQuantity,
    isTreadwellTabSelected = false,
    onAddProductClick,
    onCheckInventoryClick,
    onQuantityChange,
    onRearQuantityChange,
    product,
    productDetailUrl,
    quantity,
    rearQuantity,
    showFirstPriorityDetails = false,
    showReportCard = false,
    totalQuantity
  } = props;

  const { rearProduct } = product || {};
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    rearProduct
  );
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);
  const showAddToCart = !articleStatusProduct?.addToCartDisabled;

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  function handleQuantityChange(e, updatedValue) {
    onQuantityChange(updatedValue);
  }

  function handleRearQuantityChange(e, updatedValue) {
    onRearQuantityChange(updatedValue);
  }

  return (
    <div className={styles['container']}>
      <div className={styles['axles']}>
        <ProductListStaggeredCardAxle
          defaultQuantity={defaultQuantity}
          label="Front"
          onQuantityChange={handleQuantityChange}
          product={product}
          quantity={quantity}
          totalQuantity={totalQuantity}
        />
        {rearProduct && (
          <ProductListStaggeredCardAxle
            defaultQuantity={defaultQuantity}
            label="Rear"
            onQuantityChange={handleRearQuantityChange}
            product={rearProduct}
            quantity={rearQuantity}
            totalQuantity={totalQuantity}
          />
        )}
      </div>

      {process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' && product && (
        <div className={styles['product-availability']}>
          {shouldShowProductArticleStatusBanner ? (
            <ProductArticleStatusBanner
              product={articleStatusProduct}
              showBoxDesign={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            />
          ) : (
            <ProductAvailability
              onCheckInventoryClick={onCheckInventoryClick}
              product={product}
              quantity={quantity}
              rearProduct={product?.rearProduct}
              rearQuantity={rearQuantity}
              showBoxDesign={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            />
          )}
        </div>
      )}

      {showReportCard &&
        (showFirstPriorityDetails ? (
          <ProductFirstPriorityDetails
            dataReportUrl={productDetailUrl}
            isCollapsible={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            onClick={() => {
              trackAction(
                'product_view_pdl_data_click',
                getProductEntryDataLayerValues(product)
              );
              setSelectedProductAvailabilityStore(product);
            }}
            product={product}
          />
        ) : (
          <ProductListReviewDetails
            isTreadwellTabSelected={isTreadwellTabSelected}
            numberOfReviews={product?.reviewSummaryData?.totalCount || 0}
            onClickReadReviewsFn={() =>
              setSelectedProductAvailabilityStore(product)
            }
            product={product}
            productDetailUrl={productDetailUrl}
          />
        ))}

      <div className={styles['promo']}>
        <ProductListCardPromos product={product} />
      </div>

      {showAddToCart && rearProduct && (
        <AddProductButton
          className={styles['add-to-btn']}
          onClick={onAddProductClick}
          productItem={{
            product,
            quantity,
            rearProduct,
            rearQuantity,
            totalQuantity
          }}
        />
      )}
    </div>
  );
}

export default ProductListStaggeredCardActions;
